import { toastError } from '@/lib/toast-error';
import { currentLocale } from '@/lib/locale';
import { useState, type ReactNode } from 'react';
import { Bell, BellRing, Check, Send, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';

/** Renderiza mrkdwn básico de Slack (*negrita* + links) como en el DM real. */
function renderSlackMrkdwn(text: string): ReactNode {
  return text.split('\n').map((line, i) => (
    <span key={i} className="block min-h-2">
      {line
        .split(/(\*[^*]+\*|https?:\/\/\S+)/g)
        .map((part, j) => {
          if (/^\*[^*]+\*$/.test(part)) return <strong key={j}>{part.slice(1, -1)}</strong>;
          if (/^https?:\/\//.test(part))
            return (
              <a key={j} href={part} target="_blank" rel="noreferrer" className="text-teal-600 underline break-all">
                {part}
              </a>
            );
          return <span key={j}>{part}</span>;
        })}
    </span>
  ));
}

interface Props {
  baja: {
    id_baja: string;
    correo: string;
    fecha_baja: string;
    reminder_sent_at?: string | null;
  };
  memberName?: string;
}

/**
 * Botón + dialog de preview del recordatorio de offboarding.
 * Muestra el mensaje exacto que se manda por DM de Slack y permite enviarlo a mano.
 * El automático lo dispara el sweep diario un día antes de la baja.
 */
export function OffboardingReminderButton({ baja, memberName }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  const alreadySent = !!baja.reminder_sent_at;

  const { data: preview, isLoading } = trpc.users.previewOffboardingReminder.useQuery(
    { correo: baja.correo },
    { enabled: open, staleTime: 5 * 60 * 1000 }
  );

  const send = trpc.users.sendOffboardingReminder.useMutation({
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(`Recordatorio enviado a ${memberName || baja.correo}`);
        utils.users.getBajasFromSheet.invalidate();
        setOpen(false);
      } else {
        toast.error(`No se pudo enviar: ${res.error ?? 'error desconocido'}`);
      }
    },
    onError: (err) => toastError(err, 'common.toast.error_al_enviar_el_recordatorio'),
  });

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-8 gap-1.5"
        onClick={() => setOpen(true)}
        title={alreadySent ? t('users.offboardingreminderbutton.recordatorio_ya_enviado_ver_reenviar') : t('users.offboardingreminderbutton.recordatorio_de_offboarding_por_slack')}
      >
        {alreadySent ? (
          <>
            <Check className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-xs">Enviado</span>
          </>
        ) : (
          <>
            <Bell className="h-3.5 w-3.5" />
            <span className="text-xs">{t('users.offboardingreminderbutton.ver_mensaje')}</span>
          </>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-brand-green" />
              {t('users.offboardingreminderbutton.recordatorio_de_offboarding')}
            </DialogTitle>
            <DialogDescription>
              <Trans i18nKey="users.offboardingreminderbutton.se_manda_por_dm_a" values={{ name: memberName || baja.correo }} components={{ b: <span className="font-medium text-foreground" /> }} />
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-10"><WinclapLoader /></div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-teal-200 bg-teal-50 dark:bg-teal-950/20 p-2.5 flex items-start gap-2 text-xs text-teal-800 dark:text-teal-300">
                <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <Trans i18nKey="users.offboardingreminderbutton.este_recordatorio_se_manda_solo" components={{ b: <span className="font-semibold" /> }} />
              </div>
              {preview && !preview.slackFound && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-2.5 flex items-start gap-2 text-xs text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  {t('users.offboardingreminderbutton.no_encontramos_una_cuenta_de_slack')}
                </div>
              )}
              {alreadySent && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-2.5 flex items-center gap-2 text-xs text-emerald-700">
                  <Check className="h-3.5 w-3.5 shrink-0" />
                  {t('users.offboardingreminderbutton.ya_se_envio_el', { date: new Date(baja.reminder_sent_at as string).toLocaleDateString(currentLocale()) })}
                </div>
              )}
              <div className="rounded-lg border bg-muted/40 p-3">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">{t('users.offboardingreminderbutton.vista_previa_del_mensaje')}</p>
                <div className="text-sm leading-relaxed">{preview?.message ? renderSlackMrkdwn(preview.message) : null}</div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={send.isPending || isLoading}
              onClick={() => send.mutate({ id_baja: baja.id_baja, correo: baja.correo, fecha_baja: baja.fecha_baja })}
            >
              {send.isPending ? <WinclapLoader size={14} /> : <Send className="h-3.5 w-3.5" />}
              {alreadySent ? t('users.offboardingreminderbutton.reenviar_ahora') : t('users.offboardingreminderbutton.enviar_ahora')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
